import React, { useState, useCallback } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';

/**
 * InboxTab — scans the top-level inbox for read conversations linked to matters,
 * displays them grouped by matter with checkboxes, and files selected ones via
 * the file-email workflow.
 */
export default function InboxTab() {
    const { userEmail } = useAddin();

    const [scanning, setScanning] = useState(false);
    const [conversations, setConversations] = useState(null); // null = not scanned yet
    const [selected, setSelected] = useState(new Set()); // Set of conversationId
    const [filing, setFiling] = useState(false);
    const [filedStatus, setFiledStatus] = useState({}); // { conversationId: 'filing'|'done'|'error' }
    const [feedback, setFeedback] = useState(null);

    // ── Scan ──
    const handleScan = useCallback(async () => {
        setScanning(true);
        setConversations(null);
        setSelected(new Set());
        setFiledStatus({});
        setFeedback(null);
        try {
            const res = await webhook.scanInbox({ userEmail });
            const convs = res.conversations || [];
            setConversations(convs);
            // Select all by default
            setSelected(new Set(convs.map(c => c.conversationId)));
        } catch (err) {
            setFeedback({ type: 'error', message: err.message || 'Scan failed' });
            setConversations([]);
        } finally {
            setScanning(false);
        }
    }, [userEmail]);

    // ── Toggle individual conversation ──
    const handleToggle = (conversationId) => {
        const next = new Set(selected);
        if (next.has(conversationId)) {
            next.delete(conversationId);
        } else {
            next.add(conversationId);
        }
        setSelected(next);
    };

    // ── File selected ──
    const handleFileSelected = useCallback(async () => {
        const toFile = (conversations || []).filter(c => selected.has(c.conversationId));
        if (toFile.length === 0) return;

        setFiling(true);
        setFeedback(null);

        for (const conv of toFile) {
            setFiledStatus(prev => ({ ...prev, [conv.conversationId]: 'filing' }));
            try {
                await webhook.fileEmail({
                    source: 'inbox_tab',
                    outlookItemIds: conv.items.map(i => i.id),
                    conversationId: conv.conversationId,
                    userEmail,
                    matterId: conv.matter.id,
                    matterNumber: conv.matter.number,
                    matterDescription: conv.matter.description,
                    clientName: conv.matter.client_name,
                    contactId: conv.matter.client?.id || conv.matter.client_id || null,
                    extractAttachments: true,
                    extractImages: false,
                });
                setFiledStatus(prev => ({ ...prev, [conv.conversationId]: 'done' }));
            } catch {
                setFiledStatus(prev => ({ ...prev, [conv.conversationId]: 'error' }));
            }
        }

        setFiling(false);
        setFeedback({ type: 'success', message: `Filing queued for ${toFile.length} conversation${toFile.length !== 1 ? 's' : ''}` });
    }, [conversations, selected, userEmail]);

    // Group conversations by matter
    const byMatter = (conversations || []).reduce((acc, conv) => {
        const key = conv.matter.id;
        if (!acc[key]) acc[key] = { matter: conv.matter, convs: [] };
        acc[key].convs.push(conv);
        return acc;
    }, {});

    const selectedCount = selected.size;
    const hasResults = conversations !== null && conversations.length > 0;

    return (
        <div className="addin-body">
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                    className="modal-submit"
                    onClick={handleScan}
                    disabled={scanning}
                    style={{ flex: 1, margin: 0 }}
                >
                    {scanning ? <><div className="btn-spinner" /> Scanning...</> : '⟳ Scan Inbox'}
                </button>
            </div>

            {/* Feedback */}
            {feedback && (
                <div className={feedback.type === 'success' ? 'success-toast' : 'error-toast'} style={{ marginBottom: '10px' }}>
                    {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                    <button className="retry-btn" onClick={() => setFeedback(null)}>×</button>
                </div>
            )}

            {/* Empty state — not yet scanned */}
            {conversations === null && !scanning && (
                <div style={{ padding: '30px 0', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.5 }}>📬</div>
                    <div style={{ fontSize: '10px', color: 'var(--grey02)', lineHeight: 1.6 }}>
                        Click <strong>Scan Inbox</strong> to find read emails<br />
                        that are linked to active matters.
                    </div>
                </div>
            )}

            {/* No results */}
            {conversations !== null && conversations.length === 0 && !scanning && (
                <div style={{ padding: '30px 0', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '10px', opacity: 0.5 }}>✅</div>
                    <div style={{ fontSize: '10px', color: 'var(--grey02)', lineHeight: 1.6 }}>
                        No unread emails linked to matters found.<br />
                        Your inbox is clear.
                    </div>
                </div>
            )}

            {/* Results grouped by matter */}
            {hasResults && (
                <>
                    <div className="section-label" style={{ marginBottom: '6px' }}>
                        {conversations.length} conversation{conversations.length !== 1 ? 's' : ''} found
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        {Object.values(byMatter).map(({ matter, convs }) => (
                            <div key={matter.id} className="info-block" style={{ marginBottom: '8px', padding: 0, overflow: 'hidden' }}>
                                {/* Matter header */}
                                <div style={{
                                    padding: '7px 12px',
                                    background: 'var(--grey05)',
                                    borderBottom: '1px solid var(--grey04)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div>
                                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--navy)' }}>
                                            {matter.client_name}
                                        </div>
                                        <div style={{ fontSize: '9px', color: 'var(--grey02)' }}>
                                            {matter.number} — {matter.description}
                                        </div>
                                        <div style={{ fontSize: '8px', color: 'var(--grey03)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <span>📁</span>
                                            <span>Filed</span>
                                            <span style={{ opacity: 0.5 }}>›</span>
                                            <span>{matter.client_name}</span>
                                            <span style={{ opacity: 0.5 }}>›</span>
                                            <span>{matter.number}</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '8px', padding: '2px 6px', background: 'var(--light-teal)', color: 'var(--teal)', borderRadius: '4px', fontWeight: 700 }}>
                                        {convs.length}
                                    </div>
                                </div>

                                {/* Conversations */}
                                {convs.map((conv, i) => {
                                    const status = filedStatus[conv.conversationId];
                                    const isChecked = selected.has(conv.conversationId);
                                    return (
                                        <label
                                            key={conv.conversationId}
                                            className="checkbox-wrap"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                                padding: '8px 12px',
                                                borderTop: i > 0 ? '1px solid var(--grey04)' : 'none',
                                                cursor: 'pointer',
                                                opacity: status === 'done' ? 0.5 : 1
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleToggle(conv.conversationId)}
                                                disabled={!!status}
                                                style={{ marginTop: '2px' }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    color: 'var(--navy)',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: '6px'
                                                }}>
                                                    <span className="truncate">{conv.subject}</span>
                                                    {status === 'filing' && <div className="btn-spinner" style={{ width: '10px', height: '10px', flexShrink: 0 }} />}
                                                    {status === 'done' && <span style={{ fontSize: '8px', color: 'var(--teal)', flexShrink: 0 }}>✓ Filed</span>}
                                                    {status === 'error' && <span style={{ fontSize: '8px', color: 'var(--orange)', flexShrink: 0 }}>⚠ Error</span>}
                                                </div>
                                                <div style={{ fontSize: '9px', color: 'var(--grey02)', display: 'flex', justifyContent: 'space-between' }}>
                                                    <span className="truncate">{conv.sender}</span>
                                                    <span style={{ flexShrink: 0 }}>
                                                        {conv.receivedDateTime ? new Date(conv.receivedDateTime).toLocaleDateString() : ''}
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: '8px', color: 'var(--grey03)', marginTop: '1px' }}>
                                                    {conv.items.length} email{conv.items.length !== 1 ? 's' : ''} in thread
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <button
                        className="modal-submit"
                        onClick={handleFileSelected}
                        disabled={filing || selectedCount === 0}
                        style={{ width: '100%' }}
                    >
                        {filing ? <><div className="btn-spinner" /> Filing...</> : `File ${selectedCount} Conversation${selectedCount !== 1 ? 's' : ''}`}
                    </button>
                </>
            )}


        </div>
    );
}
